import CONFIG from '../../api/config';

const FINI_SCRIPT_ID = 'FiniWidget';
const FINI_ROOT_ID = 'fini-widget-root';

// Fini internals from its CDN bundle; they can change without notice, so every step no-ops if the markup moves.
const FINI_IFRAME_ID = 'fini-widget-iframe';
const FINI_LAUNCHER_SELECTOR = `#${FINI_ROOT_ID} .fw-cw-button`;
const FINI_PANEL_SELECTOR = '.fw-cw-framer-container-v2';
const FINI_FOOTER_LINK_SELECTOR = 'a[href="https://usefini.com"]';
const FINI_SEND_BUTTON_SELECTOR = '.fw-cw-input-bar-button';
const FINI_TOOLTIP_TEXT = 'Fini Widget';

const BRAND_STYLE_ID = 'thFiniBrandStyle';
const SEND_BUTTON_COLOR = '#0A0EFF';

const LAUNCHER_MAX_ATTEMPTS = 30;
const LAUNCHER_RETRY_MS = 100;
const CLOSE_DEBOUNCE_MS = 500;

// Elements with this attribute toggle the widget themselves, so pressing them must not count as an outside click.
const FINI_TRIGGER_SELECTOR = '[data-fini-trigger]';

let loadPromise = null;

const configureFiniSettings = () => {
    window.finiWidgetOptions = {
        widgetId: CONFIG.FINI_WIDGET_ID,
        mode: 'widget',
        // Identifying users requires a JWT signed server-side with the widget's secret key;
        // never sign it in the browser.
        customerToken: '',
        notifications: {
            sound: true,
            browser: 'ask',
            pulse: true
        }
    };
};

const ensureWidgetRoot = () => {
    if (document.getElementById(FINI_ROOT_ID)) {
        return;
    }

    const root = document.createElement('div');
    root.id = FINI_ROOT_ID;
    document.body.appendChild(root);
};

const loadFiniScript = () => {
    if (loadPromise) {
        return loadPromise;
    }

    loadPromise = new Promise((resolve, reject) => {
        if (document.getElementById(FINI_SCRIPT_ID)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.id = FINI_SCRIPT_ID;
        script.src = CONFIG.FINI_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            script.remove();
            loadPromise = null;
            reject(new Error('Failed to load Fini widget script'));
        };
        document.body.appendChild(script);
    });

    return loadPromise;
};

const getIframeDocument = iframe => {
    try {
        return iframe.contentDocument;
    } catch (error) {
        return null;
    }
};

const labelPoweredByLink = iframe => {
    const iframeDocument = getIframeDocument(iframe);
    if (!iframeDocument || !iframeDocument.body) {
        return;
    }

    const applyTitle = () => {
        const link = iframeDocument.querySelector(FINI_FOOTER_LINK_SELECTOR);
        if (!link) {
            return false;
        }
        link.title = FINI_TOOLTIP_TEXT;
        return true;
    };

    if (applyTitle()) {
        return;
    }

    const observer = new MutationObserver(() => {
        if (applyTitle()) {
            observer.disconnect();
        }
    });
    observer.observe(iframeDocument.body, { childList: true, subtree: true });
};

// The chat renders inside Fini's iframe, so page styles can't reach it.
const injectBrandStyles = iframe => {
    const iframeDocument = getIframeDocument(iframe);
    if (
        !iframeDocument ||
        !iframeDocument.head ||
        iframeDocument.getElementById(BRAND_STYLE_ID)
    ) {
        return;
    }

    const style = iframeDocument.createElement('style');
    style.id = BRAND_STYLE_ID;
    style.textContent = `${FINI_SEND_BUTTON_SELECTOR}:not(:disabled) { background-color: ${SEND_BUTTON_COLOR}; }`;
    iframeDocument.head.appendChild(style);
};

const enhanceIframe = (iframe, onFocusLeave) => {
    // The iframe title shows a native tooltip over the whole chat, so it moves to the "Powered by Fini" link.
    // Fini re-applies the title on re-render, hence the observer.
    const replaceTitleWithLabel = () => {
        if (iframe.hasAttribute('title')) {
            iframe.removeAttribute('title');
        }
        if (iframe.getAttribute('aria-label') !== FINI_TOOLTIP_TEXT) {
            iframe.setAttribute('aria-label', FINI_TOOLTIP_TEXT);
        }
    };
    replaceTitleWithLabel();
    new MutationObserver(replaceTitleWithLabel).observe(iframe, {
        attributes: true,
        attributeFilter: ['title']
    });

    const onReady = () => {
        injectBrandStyles(iframe);
        labelPoweredByLink(iframe);
        if (iframe.contentWindow) {
            iframe.contentWindow.addEventListener('blur', onFocusLeave);
        }
    };
    iframe.addEventListener('load', onReady);

    const iframeDocument = getIframeDocument(iframe);
    if (iframeDocument && iframeDocument.readyState === 'complete') {
        onReady();
    }
};

// The closed panel is scaled to zero, so it only takes up space while open.
const isPanelOpen = panel => panel.getBoundingClientRect().width > 0;

// Presses inside an iframe (the chat itself, or page content such as Coach Home) never reach this
// document, so moving focus into another iframe is treated as an outside click too.
const closeOnOutsideInteraction = root => {
    let ignoreUntil = 0;

    const closePanel = () => {
        const panel = root.querySelector(FINI_PANEL_SELECTOR);
        const launcher = root.querySelector('.fw-cw-button');
        if (
            !panel ||
            !launcher ||
            !isPanelOpen(panel) ||
            Date.now() < ignoreUntil
        ) {
            return;
        }
        // Several events can report the same click; a second toggle would reopen the panel.
        ignoreUntil = Date.now() + CLOSE_DEBOUNCE_MS;
        launcher.click();
    };

    const isInsidePanel = element => {
        const panel = root.querySelector(FINI_PANEL_SELECTOR);
        return Boolean(panel && panel.contains(element));
    };

    const onPointerDown = event => {
        const target = event.target;
        if (
            !(target instanceof Element) ||
            isInsidePanel(target) ||
            target.closest(FINI_TRIGGER_SELECTOR)
        ) {
            return;
        }
        closePanel();
    };

    const onFocusLeave = () => {
        setTimeout(() => {
            const active = document.activeElement;
            if (
                active &&
                active.tagName === 'IFRAME' &&
                !isInsidePanel(active)
            ) {
                closePanel();
            }
        }, 0);
    };

    const guard = handler => event => {
        if (!root.isConnected) {
            document.removeEventListener(
                'pointerdown',
                guardedPointerDown,
                true
            );
            window.removeEventListener('blur', guardedFocusLeave);
            return;
        }
        handler(event);
    };
    const guardedPointerDown = guard(onPointerDown);
    const guardedFocusLeave = guard(onFocusLeave);

    document.addEventListener('pointerdown', guardedPointerDown, true);
    window.addEventListener('blur', guardedFocusLeave);
    return guardedFocusLeave;
};

const enhanceWidgetChrome = () => {
    const root = document.getElementById(FINI_ROOT_ID);
    if (!root || root.dataset.thEnhanced === 'true') {
        return;
    }
    root.dataset.thEnhanced = 'true';
    const onFocusLeave = closeOnOutsideInteraction(root);

    const existing = document.getElementById(FINI_IFRAME_ID);
    if (existing) {
        enhanceIframe(existing, onFocusLeave);
        return;
    }

    const observer = new MutationObserver(() => {
        const iframe = document.getElementById(FINI_IFRAME_ID);
        if (iframe) {
            observer.disconnect();
            enhanceIframe(iframe, onFocusLeave);
        }
    });
    observer.observe(root, { childList: true, subtree: true });
};

export const initFiniWidget = () => {
    configureFiniSettings();
    ensureWidgetRoot();
    enhanceWidgetChrome();
    return loadFiniScript();
};

export const openFiniWidget = () => {
    const clickWhenReady = attempt => {
        const launcher = document.querySelector(FINI_LAUNCHER_SELECTOR);
        if (launcher) {
            launcher.click();
            return;
        }
        if (attempt < LAUNCHER_MAX_ATTEMPTS) {
            setTimeout(() => clickWhenReady(attempt + 1), LAUNCHER_RETRY_MS);
        }
    };

    // Waits for the script so the retries cover the launcher rendering, not the download.
    // Errors are swallowed because this is wired straight to an onClick.
    return initFiniWidget()
        .then(() => clickWhenReady(0))
        .catch(() => {});
};

export default { initFiniWidget, openFiniWidget };
